/**
 * Dictionary of common backend error translations
 * Maps English error messages to user-friendly Spanish messages
 */

const EXACT_ERROR_TRANSLATIONS: Record<string, string> = {
  // Authentication errors
  Unauthorized: 'No autorizado. Por favor, inicia sesión nuevamente.',
  'Invalid token': 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
  'Token expired': 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
  'Access denied': 'No tienes permisos para realizar esta acción.',
  Forbidden: 'No tienes permisos para realizar esta acción.',

  // Validation errors
  'Validation error': 'Error de validación. Por favor, revisa los datos ingresados.',
  'Invalid request': 'Los datos enviados no son válidos.',
  'Bad request': 'Los datos enviados no son válidos. Por favor, revisa la información.',
  'Missing required fields': 'Faltan campos requeridos. Por favor, completa todos los campos.',

  // Pickup/collection errors
  'Pickup creation failed': 'No se pudo crear la recolección. Por favor, intenta nuevamente.',
  'Invalid pickup address': 'La dirección de recolección no es válida.',
  'Pickup address not covered': 'La dirección de recolección no está dentro de la cobertura.',
  'Address not covered': 'La dirección no está dentro de nuestra cobertura.',
  'No coverage for this address': 'No hay cobertura disponible para esta dirección.',
  'Invalid postal code': 'El código postal no es válido.',
  'Postal code not found': 'El código postal no fue encontrado.',
  'Invalid phone number': 'El número de teléfono no es válido.',
  'Phone number is required': 'El número de teléfono es requerido.',

  // Inventory/product errors
  'Product not found': 'El producto no fue encontrado.',
  'SKU not found': 'El SKU no fue encontrado en el inventario.',
  'Invalid SKU': 'El SKU ingresado no es válido.',
  'Insufficient inventory': 'No hay suficiente inventario disponible.',
  'Inventory not available': 'El inventario no está disponible.',
  'Duplicate SKU': 'El SKU ya existe en el sistema.',
  'Product already exists': 'El producto ya existe en el sistema.',

  // Warehouse errors
  'Warehouse not found': 'El almacén seleccionado no fue encontrado.',
  'Invalid warehouse': 'El almacén seleccionado no es válido.',
  'Warehouse is full': 'El almacén está lleno. Selecciona otro almacén.',
  'Warehouse not available': 'El almacén no está disponible actualmente.',
  main_warehouse: 'No tienes un almacén activo. Por favor, activa o asigna un almacén.',
  'main warehouse': 'No tienes un almacén activo. Por favor, activa o asigna un almacén.',
  'main_warehouse not found':
    'No se encontró un almacén activo. Por favor, verifica tu configuración.',
  'main_warehouse inactive': 'Tu almacén no está activo. Por favor, actívalo para continuar.',
  'no main warehouse': 'No tienes un almacén configurado. Por favor, asigna uno para continuar.',

  // Date/appointment errors
  'Invalid appointment date': 'La fecha de cita no es válida.',
  'Appointment date is in the past': 'La fecha de cita no puede ser en el pasado.',
  'Date must be in the future': 'La fecha debe ser posterior a hoy.',
  'Invalid date format': 'El formato de fecha no es válido.',
  'Appointment slot not available': 'El horario seleccionado no está disponible.',

  // Arrival order errors
  'Arrival order creation failed':
    'No se pudo crear la orden de arribo. Por favor, intenta nuevamente.',
  'Arrival order not found': 'La orden de arribo no fue encontrada.',
  'Order already exists': 'Ya existe una orden con estos datos.',
  'Duplicate order': 'Esta orden ya fue creada anteriormente.',

  // Connection/server errors
  'Network error': 'Error de conexión. Por favor, verifica tu internet e intenta nuevamente.',
  'Internal server error': 'Error interno del servidor. Por favor, intenta más tarde.',
  'Service unavailable': 'El servicio no está disponible temporalmente.',
  'Gateway timeout': 'El servidor tardó demasiado en responder. Por favor, intenta nuevamente.',
  'Request timeout': 'La solicitud tardó demasiado. Por favor, intenta nuevamente.',
  'Too many requests': 'Demasiadas solicitudes. Por favor, espera un momento e intenta de nuevo.',

  // Package errors
  'Invalid package size': 'El tamaño de paquete seleccionado no es válido.',
  'Package size is required': 'Debes seleccionar un tamaño de paquete.',
  'Weight exceeds limit': 'El peso excede el límite permitido.',
  'Dimensions exceed limit': 'Las dimensiones exceden el límite permitido.',

  // Country errors
  'Country not supported': 'El país seleccionado no está soportado.',
  'Invalid country code': 'El código de país no es válido.',

  // Generic errors
  'Something went wrong': 'Algo salió mal. Por favor, intenta nuevamente.',
  'An error occurred': 'Ha ocurrido un error. Por favor, intenta nuevamente.',
  'Unknown error': 'Ha ocurrido un error desconocido.',
  'Operation failed': 'La operación falló. Por favor, intenta nuevamente.',
};

const ERROR_PATTERNS: Array<{ pattern: RegExp; translation: string }> = [
  // Required field errors
  { pattern: /field ['"]?(\w+)['"]? is required/i, translation: 'El campo {field} es requerido.' },
  { pattern: /['"]?(\w+)['"]? is required/i, translation: 'El campo {field} es requerido.' },
  { pattern: /missing ['"]?(\w+)['"]?/i, translation: 'Falta el campo {field}.' },

  // Field validation errors
  { pattern: /invalid ['"]?(\w+)['"]?/i, translation: 'El campo {field} no es válido.' },
  {
    pattern: /['"]?(\w+)['"]? must be/i,
    translation: 'El campo {field} tiene un formato incorrecto.',
  },
  {
    pattern: /['"]?(\w+)['"]? cannot be empty/i,
    translation: 'El campo {field} no puede estar vacío.',
  },

  // Duplicate errors
  {
    pattern: /duplicate.*['"]?(\w+)['"]?/i,
    translation: 'Ya existe un registro con este {field}.',
  },
  {
    pattern: /['"]?(\w+)['"]? already exists/i,
    translation: 'El {field} ya existe en el sistema.',
  },

  // Not found errors
  { pattern: /['"]?(\w+)['"]? not found/i, translation: 'No se encontró el {field} especificado.' },
  { pattern: /could not find ['"]?(\w+)['"]?/i, translation: 'No se pudo encontrar el {field}.' },

  // Coverage errors
  { pattern: /no coverage/i, translation: 'No hay cobertura disponible para esta ubicación.' },
  { pattern: /out of coverage/i, translation: 'La ubicación está fuera de cobertura.' },
  { pattern: /not covered/i, translation: 'Esta ubicación no está dentro de nuestra cobertura.' },

  // Limit errors
  { pattern: /exceeds? (the )?limit/i, translation: 'Se ha excedido el límite permitido.' },
  { pattern: /maximum.*exceeded/i, translation: 'Se ha excedido el máximo permitido.' },
  { pattern: /minimum.*required/i, translation: 'No se cumple el mínimo requerido.' },

  // Permission errors
  { pattern: /permission denied/i, translation: 'No tienes permisos para realizar esta acción.' },
  { pattern: /not authorized/i, translation: 'No estás autorizado para realizar esta acción.' },
  { pattern: /access.*denied/i, translation: 'Acceso denegado.' },

  // Connection errors
  {
    pattern: /connection.*failed/i,
    translation: 'Error de conexión. Por favor, intenta nuevamente.',
  },
  {
    pattern: /timeout/i,
    translation: 'La operación tardó demasiado. Por favor, intenta nuevamente.',
  },
  { pattern: /network.*error/i, translation: 'Error de red. Por favor, verifica tu conexión.' },

  // Warehouse errors
  {
    pattern: /main.?warehouse/i,
    translation: 'No tienes un almacén activo. Por favor, activa o asigna un almacén.',
  },

  // Format errors
  { pattern: /invalid.*format/i, translation: 'El formato ingresado no es válido.' },
  { pattern: /format.*invalid/i, translation: 'El formato no es válido.' },
];

// Mapping of field names from English to Spanish
const FIELD_TRANSLATIONS: Record<string, string> = {
  address: 'dirección',
  city: 'ciudad',
  country: 'país',
  phone: 'teléfono',
  email: 'correo electrónico',
  name: 'nombre',
  firstName: 'nombre',
  lastName: 'apellido',
  zipcode: 'código postal',
  postalCode: 'código postal',
  postal_code: 'código postal',
  sku: 'SKU',
  quantity: 'cantidad',
  warehouse: 'almacén',
  warehouseId: 'almacén',
  appointment: 'fecha de cita',
  date: 'fecha',
  dateTime: 'fecha y hora',
  scheduledDate: 'fecha programada',
  packageSize: 'tamaño de paquete',
  package_size: 'tamaño de paquete',
  weight: 'peso',
  reference: 'referencia',
  notes: 'notas',
  parcels: 'bultos',
  contact: 'contacto',
  origin: 'origen',
  destination: 'destino',
  products: 'productos',
  items: 'artículos',
  pickup: 'recolección',
  delivery: 'entrega',
  shipment: 'envío',
  order: 'orden',
  tracking: 'rastreo',
  status: 'estado',
  price: 'precio',
  amount: 'monto',
  total: 'total',
  length: 'longitud',
  width: 'ancho',
  height: 'alto',
  thumbnail: 'imagen principal',
  description: 'descripción',
  category: 'categoría',
  barcode: 'código de barras',
  brand: 'marca',
  title: 'título',
};

/**
 * Translates a field name from English to Spanish
 */
const translateFieldName = (field: string): string => {
  const lowerField = field.toLowerCase();
  return FIELD_TRANSLATIONS[lowerField] || FIELD_TRANSLATIONS[field] || field;
};

/**
 * Translates a Joi-style validation error message

 * e.g. `""length"" is a required field` -> `El campo longitud es requerido.`

 */
const translateJoiFieldMessage = (message: string): string | null => {
  const joiRequiredMatch = message.match(/["']{1,2}([^"']+)["']{1,2}\s+is a required field/i);
  if (joiRequiredMatch) {
    const fieldName = joiRequiredMatch[1].trim();
    const translatedField = translateFieldName(fieldName);
    return `El campo "${translatedField}" es requerido.`;
  }

  const joiMustBeMatch = message.match(/["']{1,2}([^"']+)["']{1,2}\s+must be/i);
  if (joiMustBeMatch) {
    const fieldName = joiMustBeMatch[1].trim();
    const translatedField = translateFieldName(fieldName);
    return `El campo "${translatedField}" tiene un formato incorrecto.`;
  }

  return null;
};

/**
 * Translates a backend error message to a user-friendly Spanish message
 * @param errorMessage - The original error message (may be in English)
 * @param defaultMessage - Default message if no translation is found
 * @returns The translated Spanish message
 */
export const translateErrorMessage = (
  errorMessage: string,
  defaultMessage = 'Ha ocurrido un error. Por favor, intenta nuevamente.',
): string => {
  if (!errorMessage) {
    return defaultMessage;
  }

  const trimmedMessage = errorMessage.trim();

  // Check for country mismatch error (e.g. "destination.country (CHL) must match shipment.country (MEX)")
  const countryMismatchMatch = trimmedMessage.match(
    /destination\.country\s*\((\w+)\)\s*must\s*match\s*shipment\.country\s*\((\w+)\)/i,
  );
  if (countryMismatchMatch) {
    const destCountry = countryMismatchMatch[1];
    const origCountry = countryMismatchMatch[2];
    return `El país de destino (${destCountry}) debe coincidir con el país de origen de tu almacén (${origCountry})`;
  }

  // First, search for the exact match
  if (EXACT_ERROR_TRANSLATIONS[trimmedMessage]) {
    return EXACT_ERROR_TRANSLATIONS[trimmedMessage];
  }

  // Search for case-insensitive exact match
  const lowerMessage = trimmedMessage.toLowerCase();
  for (const [key, value] of Object.entries(EXACT_ERROR_TRANSLATIONS)) {
    if (key.toLowerCase() === lowerMessage) {
      return value;
    }
  }

  const joiTranslation = translateJoiFieldMessage(trimmedMessage);
  if (joiTranslation) {
    return joiTranslation;
  }

  // Search for pattern match
  for (const { pattern, translation } of ERROR_PATTERNS) {
    const match = trimmedMessage.match(pattern);
    if (match) {
      // If there's a capture group (field name), replace it
      if (match[1]) {
        const translatedField = translateFieldName(match[1]);
        return translation.replace('{field}', translatedField);
      }
      return translation;
    }
  }

  const spanishIndicators = /[áéíóúñ¿¡]/i;
  const englishIndicators =
    /\b(the|is|are|was|were|not|found|invalid|failed|must|should|cannot|required)\b/i;

  if (spanishIndicators.test(trimmedMessage) && !englishIndicators.test(trimmedMessage)) {
    return trimmedMessage;
  }

  if (englishIndicators.test(trimmedMessage)) {
    return defaultMessage;
  }

  return trimmedMessage;
};

/**
 * Translates an array of affected pickup errors
 */
export const translateAffectedPickupErrors = (
  affectedPickups: Array<{ description: string; error: string }>,
): Array<{ description: string; error: string }> => {
  return affectedPickups.map((pickup) => ({
    description: pickup.description,
    error: translateErrorMessage(pickup.error),
  }));
};

/**
 * Creates a formatted error message for display in toast
 * Includes the main message and optionally additional details
 */
export const formatErrorForToast = (
  mainMessage: string,
  details?: { affectedItems?: number; requestId?: string },
): string => {
  let formattedMessage = translateErrorMessage(mainMessage);

  if (details?.affectedItems && details.affectedItems > 0) {
    formattedMessage += ` (${details.affectedItems} elemento${details.affectedItems > 1 ? 's' : ''} afectado${details.affectedItems > 1 ? 's' : ''})`;
  }

  if (details?.requestId) {
    formattedMessage += ` (ID de solicitud: ${details.requestId})`;
  }

  return formattedMessage;
};

/**
 * Formats import product errors into a single readable string
 * Handles both validation errors (field-level) and business errors (product already exists, etc.)
 */
export const formatImportProductErrors = (
  errors: Array<{ sku?: string; error?: string; field?: string; message?: string }>,
): string => {
  if (!errors || errors.length === 0) {
    return 'Error al importar los productos.';
  }

  const seen = new Set<string>();
  const lines: string[] = [];

  for (const err of errors) {
    let line: string;

    if (err.sku && err.error) {
      line = `• SKU ${err.sku}: ${translateErrorMessage(err.error)}`;
    } else if (err.field && err.message) {
      line = `• ${translateErrorMessage(err.message)}`;
    } else {
      line = `• ${translateErrorMessage(err.message ?? err.error ?? 'Error desconocido')}`;
    }

    if (!seen.has(line)) {
      seen.add(line);
      lines.push(line);
    }
  }

  return lines.join('\n');
};

/**
 * Translates a single import error item into a user-friendly Spanish string.
 * Handles both field-level validation errors and business errors (SKU-level).
 */
export const translateImportError = (err: {
  sku?: string;
  error?: string;
  field?: string;
  message?: string;
  type?: string;
}): string => {
  if (err.field && err.message) {
    return translateErrorMessage(err.message);
  }
  if (err.error) {
    return translateErrorMessage(err.error);
  }
  if (err.message) {
    return translateErrorMessage(err.message);
  }
  return 'Error desconocido.';
};

export default translateErrorMessage;
